import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  HostListener,
  Input,
  inject,
  input,
  model,
  type OnInit,
  Output,
  signal,
  type TemplateRef,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FinoCommonModule } from '@fino-ui/common';
import { FinoIconModule } from '@fino-ui/icon';
import { FinoTranslationModule } from '@fino-ui/translation';
import { first } from 'lodash';
import { ClipboardService } from 'ngx-clipboard';
import { NgxMaskDirective } from 'ngx-mask';

@Component({
  selector: 'app-typeahead',
  templateUrl: './typeahead.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    FinoIconModule,
    FinoCommonModule,
    FinoTranslationModule,
    NgTemplateOutlet,
  ],
})
export class TypeaheadComponent implements OnInit {
  private readonly clipboardService = inject(ClipboardService);

  readonly form = input<any>(undefined);
  readonly controlName = input<string>('');
  readonly label = input<string>(undefined);
  readonly disableTypeahead = input<boolean>(false);

  @Input() results: any;
  readonly resultTemplate = input.required<TemplateRef<any>>();
  readonly resultSelected = input<EventEmitter<any>>(new EventEmitter());
  loading = model(false);
  readonly mask = input<string>('');
  readonly canNavigateByKeys = input(false);
  readonly copyWithoutWhitespace = input(false);

  @Output() keyboardSelected = new EventEmitter();
  @Output() searchChanged = new EventEmitter();

  private readonly resultsVisible = signal(false);

  public readonly showResults = computed(
    () => this.resultsVisible() && !this.disableTypeahead(),
  );

  public selectedIndex = signal(0);

  public hasFocus = signal(false);

  get value() {
    return this.form()?.controls[this.controlName()]?.value;
  }

  get hasValue() {
    return this.value;
  }

  get hasDanger() {
    const control = this.form().controls[this.controlName()];
    return control.touched && control.errors;
  }

  get error(): string {
    const control = this.form().controls[this.controlName()];
    return `error.${first(Object.keys(control.errors))}`;
  }

  ngOnInit() {
    this.resultSelected().subscribe((_r) => {
      this.resultsVisible.set(false);
      this.loading.set(false);
      this.selectedIndex.set(0);
    });
  }

  typing() {
    if (this.disableTypeahead()) return;
    this.resultsVisible.set(true);
    this.searchChanged.next(this.value);
  }

  onFocus() {
    this.hasFocus.set(true);
  }

  onBlur() {
    this.hasFocus.set(false);
  }

  copyToClipboard() {
    const toCopy = this.copyWithoutWhitespace()
      ? this.value.replace(' ', '')
      : this.value;
    this.clipboardService.copy(toCopy);
  }

  @HostListener('document:keydown.escape') onKeydownHandler() {
    this.resultsVisible.set(false);
  }

  onKeyDown($event: any) {
    switch ($event.key) {
      case 'ArrowDown':
        this.selectedIndex.update((i) => i + 1);
        break;
      case 'ArrowUp':
        this.selectedIndex.update((i) => i - 1);
        break;
      case 'Enter':
        this.keyboardSelected.next(this.selectedIndex());
        break;
    }

    if (this.selectedIndex() < 0) {
      this.selectedIndex.set(this.results.length - 1);
    }
    if (this.selectedIndex() >= this.results.length) {
      this.selectedIndex.set(0);
    }
  }

  hideResults() {
    this.selectedIndex.set(0);
    this.loading.set(false);
    this.resultsVisible.set(false);
  }
}
